import { createFileRoute } from "@tanstack/react-router";
import { FileSignature, FileText, ShieldCheck } from "lucide-react";
import { FeaturePage } from "@/components/marketing/FeaturePage";
import { AiToolBanner } from "@/components/marketing/AiGeneratorPage";
import { alternateLinkList } from "@/lib/i18n/locale";
import heroImg from "@/assets/contracts-hero.webp";
import templatesImg from "@/assets/contracts-templates.webp";
import signingImg from "@/assets/contracts-signing.webp";

const IMG: Record<string, string> = { hero: heroImg, templates: templatesImg, signing: signingImg };

export const Route = createFileRoute("/el/platform/contracts")({
  head: () => ({
    meta: [
      { title: "Συμβάσεις & ηλεκτρονική υπογραφή — banana.cards" },
      { name: "description", content: "Στείλτε νομικά έγκυρες συμβάσεις σε λίγα λεπτά. Παρακολουθήστε την κατάσταση, υπογράψτε και αρχειοθετήστε — όλα σε ένα μέρος." },
      { property: "og:title", content: "Συμβάσεις & ηλεκτρονική υπογραφή — banana.cards" },
      { property: "og:description", content: "Gửi hợp đồng có giá trị pháp lý trong vài phút." },
      { property: "og:image", content: IMG['hero'] },
      { property: "og:locale", content: "el_GR" },
    ],
    links: alternateLinkList("/platform/contracts", "el"),
  }),
  component: Page,
});

function Page() {
  return (
    <FeaturePage
      eyebrow="Συμβάσεις & ηλεκτρονική υπογραφή"
      title="Στείλτε. Υπογράψτε."
      italic="Έτοιμο."
      topBanner={<AiToolBanner to="/ai/contract-generator" label="Thử AI Contract Generator mới — miễn phí" sublabel="Mô tả deal, nhận hợp đồng có thể ký trong vài giây." />}
      description="Επιλέξτε πρότυπο, προσαρμόστε το και στείλτε το. Οι πελάτες υπογράφουν από κάθε συσκευή — με νομική ισχύ, audit trail και ασφαλή αρχειοθέτηση."
      heroImage={IMG['hero']}
      metrics={[{ v: "eIDAS", l: "tuân thủ" }, { v: "90 giây", l: "để gửi" }, { v: "100%", l: "audit-trail" }]}
      bullets={[
        { icon: FileText, title: "Template sẵn có", desc: "Template đã được thử nghiệm cho sales, freelance, bất động sản và hơn nữa." },
        { icon: FileSignature, title: "Ký ở mọi nơi", desc: "Gõ, vẽ hoặc tải chữ ký — trên desktop hoặc mobile." },
        { icon: ShieldCheck, title: "Audit-trail & bảo mật", desc: "PDF chống giả mạo, log audit đầy đủ, tuân thủ GDPR & eIDAS." }
      ]}
      deepDives={[
        { eyebrow: "Template", title: "Từ nháp đến ký,", italic: "trong 90 giây.", description: "Chọn template, lấy δεδομένα khách từ CRM, và gửi. Tự điền tên, giá và ngày tháng để bạn không lặp lại công elệc.", image: IMG["templates"], reverse: false, points: ["50+ template có sẵn", "Biến tự điền từ CRM", "Điều khoản và trường có điều kiện", "Branding tùy chỉnh theo template"] },
        { eyebrow: "Ký", title: "Không ma sát cho", italic: "phía bên kia.", description: "Người nhận ký từ mọi thiết bị — không cần tài khoản, không cần app. Trạng thái cập nhật về CRM thời gian thực.", image: IMG["signing"], reverse: true, points: ["Không cần đăng nhập", "Tối ưu mobile", "Người ký tuần tự & song song", "Webhook cho mọi bước"] }
      ]}
      howItWorks={[
        { title: "Chọn template", desc: "Hoặc tải PDF của bạn." },
        { title: "Điền từ CRM", desc: "Biến tự điền từ liên hệ." },
        { title: "Gửi để ký", desc: "Email, link hoặc SMS." },
        { title: "Nhận thông báo", desc: "PDF có audit-trail vào vault." }
      ]}
      capabilities={["Trường chữ ký kéo-thả", "Người ký tuần tự & duyệt", "Branding & màu tùy chỉnh", "Thanh toán tích hợp khi ký", "Nhắc tự động & hết hạn", "Vault hợp đồng tìm kiếm được", "Tích hợp Webhook & Zapier", "Có giá trị pháp lý toàn cầu"]}
      faq={[
        { q: "Chữ ký điện tử Banana có giá trị pháp lý không?", a: "Có. Chữ ký tuân thủ ESIGN Act (Mỹ), UETA và quy định eIDAS (EU), được chấp nhận tại tòa án ở hầu hết các khu vực pháp lý lớn. Mỗi tài liệu đã ký có audit-trail chống giả mạo." },
        { q: "Người nhận có cần tài khoản không?", a: "Không — họ ký trực tiếp từ link, không cần cài app hay đăng ký." },
        { q: "Có thể nhận thanh toán khi ký không?", a: "Có — kết nối Stripe để tính phí khi hợp đồng được ký, hoàn hảo cho deposit dịch vụ và đơn đặt hàng." },
        { q: "Có thể tạo hợp đồng bằng AI không?", a: "Có — AI Contract Generator elết hợp đồng từ mô tả deal của bạn, được luật sư xem xét và tuân thủ pháp lý địa phương." }
      ]}
      outro={{ title: "Ngừng đuổi theo chữ ký.", italic: "Bắt đầu chốt deal.", description: "Hợp đồng đi từ gửi đến ký nhanh như cuộc trò chuyện." }}
    />
  );
}